/**
 * Which assistant gets the credit for a conversion.
 *
 * The only screen in this product that connects an answer engine to something
 * the customer's business counts. Everything else says a brand was named or
 * cited; this says somebody came and did the thing.
 *
 * Two models, because they answer two different questions and a single number
 * would be a choice made on the customer's behalf: first-click is which
 * assistant introduced them, last-click is which one they came back through.
 */

import { NativeSelect, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { engineLabel, rate, SourceNote, TrafficEmpty, Unavailable } from "~/modules/analytics/pages/tracking/shared";

type Model = "first" | "last";

/** The Attribution tab of AI referrals. */
export function Attribution({ entityId, range }: { entityId: string; range: string }) {
	const t = useT();
	const { locale } = useI18n();
	const [model, setModel] = useState<Model>("first");
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "traffic", "attribution", range, model],
		queryFn: () => api.trafficAttribution(entityId, { range, model }),
		...read,
	});

	const goals = useQuery({
		queryKey: [...keys.entity(entityId), "traffic", "goals", range],
		queryFn: () => api.trafficGoals(entityId, { range }),
		enabled: Boolean(state.data?.available && !state.data?.setup_required),
		...read,
	});

	return (
		<Async state={state}>
			{(data) => (
				<>
					<SourceNote frame={data} />
					{!data.available || data.setup_required ? (
						<Unavailable frame={data} entityId={entityId} />
					) : (
						<Async state={goals}>
							{(goalData) =>
								goalData.goals.length === 0 && !data.credits.some((credit) => credit.conversions > 0) ? (
									<TrafficEmpty
										title="goals.empty"
										body="goals.firstBody"
										entityId={entityId}
										destination="goals"
										action="attribution.emptyAction"
									/>
								) : (
									<Section
										title={t("attribution.credits")}
										lede={t(`attribution.${model}Hint`)}
										action={
											// A select, not a tab strip: choosing a model
											// changes what the table means rather than
											// navigating, and this screen's tabs already
											// are the navigation.
											<NativeSelect
												value={model}
												aria-label={t("attribution.credits")}
												onChange={(event) => setModel(event.target.value as Model)}
											>
												<option value="first">{t("attribution.first")}</option>
												<option value="last">{t("attribution.last")}</option>
											</NativeSelect>
										}
									>
										{data.credits.length === 0 ? (
											<TrafficEmpty
												title="attribution.noConversions"
												body="attribution.noConversionsBody"
												entityId={entityId}
												destination="goals"
												action="goals.checkRules"
											/>
										) : (
											<div className="table-scroll">
												<Table variant="resource">
													<TableHeader>
														<TableRow>
															<TableHead className="wrap">{t("traffic.engine")}</TableHead>
															<TableHead className="num">{t("traffic.sessions")}</TableHead>
															<TableHead className="num">{t("traffic.conversions")}</TableHead>
															<TableHead className="num">{t("attribution.rate")}</TableHead>
														</TableRow>
													</TableHeader>
													<TableBody>
														{data.credits.map((credit) => (
															<TableRow key={credit.engine}>
																<TableCell className="wrap">{engineLabel(t, credit.engine)}</TableCell>
																<TableCell className="num">{fmt.number(locale, credit.sessions)}</TableCell>
																<TableCell className="num">{fmt.number(locale, credit.conversions)}</TableCell>
																{/* Sessions can be zero where the credited
														    first touch fell outside this window, and a
														    rate over nothing is an em dash, never 0%. */}
																<TableCell className="num">
																	{rate(locale, credit.sessions > 0 ? credit.conversions / credit.sessions : null)}
																</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</div>
										)}
									</Section>
								)
							}
						</Async>
					)}
				</>
			)}
		</Async>
	);
}
